import React, { FC, useState, useCallback, useEffect } from 'react';
import useQueryParameters from 'hooks/useQueryParameters';
import LookupPrescreenId from './LookupPrescreenId';
import FormPrescreenId from './FormPrescreenId';

import useVirtualLookup, {
  VirtualLookupError,
} from 'hooks/requests/virtualLookup';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import { useTrackApplicationStart } from 'hooks/useAnalytics';
import { useDispatch, useSelector } from 'react-redux';
import {
  setVirtualLookupSuccessful,
  selectVirtualLookupSuccessful,
} from 'state/prescreen';
import { NonClientError } from 'hooks/requests/fetchUtils';

/**
 * Maxmimum number of times the user can try the Virtual Lookup
 */
const MAX_ATTEMPTS = 3;

const BatchPrescreenStart: FC = () => {
  useTrackApplicationStart();
  const { prescreenId } = useQueryParameters();
  const [virtualLookup] = useVirtualLookup();
  const { push } = useNavigationHelpers();

  const { pathAfter } = useWorkflow();
  const [attempts, setAttempts] = useState<number>(0);
  const dispatch = useDispatch();
  const successfulVirtualLookup = useSelector(selectVirtualLookupSuccessful);

  const makeVirtualLookupCall = useCallback(
    async (prescreenIdInput: string) => {
      try {
        const response = await virtualLookup({ prescreenId: prescreenIdInput });
        setAttempts(attempts => attempts + 1);
        if (response.hasExistingAccount) {
          push('/existing-account');
        } else if (response.isExpired) {
          push('/batch-prescreen/expired');
        } else {
          // The new value for virtualLookupSuccessful is only propagated to the
          // components and hooks on the next render cycle. So looking up the
          // next step immediately here may return the incorrect step.
          //
          // We work around this by only setting the state, and on the next
          // render cycle (when the workflow hooks has the correct value for
          // virtualLookupSuccessful), lookup the next step and navigate.
          dispatch(setVirtualLookupSuccessful(true));
        }
      } catch (e) {
        if (!(e instanceof VirtualLookupError || e instanceof NonClientError)) {
          console.error(e);
        }
        if (prescreenId) {
          push('/batch-prescreen/error');
        } else {
          setAttempts(attempts => attempts + 1);
        }
      }
    },
    [dispatch, setAttempts, virtualLookup, prescreenId, push],
  );

  useEffect(() => {
    if (attempts >= MAX_ATTEMPTS) {
      push('/batch-prescreen/error');
    } else if (successfulVirtualLookup) {
      push(pathAfter(Step.BATCH_PRESCREEN_START));
    }
  }, [push, pathAfter, successfulVirtualLookup, attempts]);

  if (attempts >= MAX_ATTEMPTS || successfulVirtualLookup) {
    // Nothing to render in this cycle.
    // After mount, the effect above will run and perform a push.
    // If we don't render null here, there will be a flash of the components
    // rendered below.
    return null;
  } else if (prescreenId && attempts === 0) {
    // If prescreenId is present with an error, we don't increment attemps.
    // If attempts are incremented for some reason, there will be a flash of FormPrescreenId
    return <LookupPrescreenId virtualLookupCall={makeVirtualLookupCall} />;
  } else {
    return (
      <FormPrescreenId
        virtualLookupCall={makeVirtualLookupCall}
        attempts={attempts}
        maxAttempts={MAX_ATTEMPTS}
      />
    );
  }
};

export default BatchPrescreenStart;
